import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { empresaAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
    Save, Copy, RotateCcw, Palette, Eye, Sun, Moon, Trash2, Upload, Image as ImageIcon,
    Type, Ruler,
} from 'lucide-react';

const THEME_KEYS = [
    { key: 'primary', label: 'Primario' },
    { key: 'secondary', label: 'Secundario' },
    { key: 'accent', label: 'Acento' },
    { key: 'bg', label: 'Fondo' },
    { key: 'surface', label: 'Superficie' },
    { key: 'text', label: 'Texto' },
    { key: 'text_muted', label: 'Texto Suave' },
    { key: 'border', label: 'Borde' },
    { key: 'sidebar_bg', label: 'Sidebar Fondo' },
    { key: 'sidebar_text', label: 'Sidebar Texto' },
    { key: 'sidebar_active', label: 'Sidebar Activo' },
    { key: 'header_bg', label: 'Header Fondo' },
    { key: 'header_text', label: 'Header Texto' },
    { key: 'success', label: 'Éxito' },
    { key: 'warning', label: 'Advertencia' },
    { key: 'danger', label: 'Peligro' },
    { key: 'info', label: 'Info' },
];

const PRESETS = {
    industrial_naranja: {
        name: 'Industrial Naranja',
        light: { primary: '#F97316', secondary: '#6366F1', accent: '#10B981', bg: '#FFFFFF', surface: '#FFF7ED', text: '#111827', text_muted: '#6B7280', border: '#FED7AA', sidebar_bg: '#FFFFFF', sidebar_text: '#374151', sidebar_active: '#F97316', header_bg: '#FFFFFF', header_text: '#111827', success: '#10B981', warning: '#F59E0B', danger: '#EF4444', info: '#3B82F6' },
        dark: { primary: '#FB923C', secondary: '#818CF8', accent: '#34D399', bg: '#111827', surface: '#1F2937', text: '#F9FAFB', text_muted: '#9CA3AF', border: '#374151', sidebar_bg: '#1F2937', sidebar_text: '#D1D5DB', sidebar_active: '#FB923C', header_bg: '#1F2937', header_text: '#F9FAFB', success: '#34D399', warning: '#FBBF24', danger: '#F87171', info: '#60A5FA' },
    },
    mineria_azul: {
        name: 'Minería Azul',
        light: { primary: '#3B82F6', secondary: '#8B5CF6', accent: '#06B6D4', bg: '#FFFFFF', surface: '#EFF6FF', text: '#111827', text_muted: '#6B7280', border: '#BFDBFE', sidebar_bg: '#FFFFFF', sidebar_text: '#374151', sidebar_active: '#3B82F6', header_bg: '#FFFFFF', header_text: '#111827', success: '#10B981', warning: '#F59E0B', danger: '#EF4444', info: '#3B82F6' },
        dark: { primary: '#60A5FA', secondary: '#A78BFA', accent: '#22D3EE', bg: '#0F172A', surface: '#1E293B', text: '#F8FAFC', text_muted: '#94A3B8', border: '#334155', sidebar_bg: '#1E293B', sidebar_text: '#CBD5E1', sidebar_active: '#60A5FA', header_bg: '#1E293B', header_text: '#F8FAFC', success: '#34D399', warning: '#FBBF24', danger: '#F87171', info: '#60A5FA' },
    },
    corporativo_verde: {
        name: 'Corporativo Verde',
        light: { primary: '#059669', secondary: '#6366F1', accent: '#0891B2', bg: '#FFFFFF', surface: '#ECFDF5', text: '#111827', text_muted: '#6B7280', border: '#A7F3D0', sidebar_bg: '#FFFFFF', sidebar_text: '#374151', sidebar_active: '#059669', header_bg: '#FFFFFF', header_text: '#111827', success: '#10B981', warning: '#F59E0B', danger: '#EF4444', info: '#3B82F6' },
        dark: { primary: '#34D399', secondary: '#818CF8', accent: '#22D3EE', bg: '#111827', surface: '#1F2937', text: '#F9FAFB', text_muted: '#9CA3AF', border: '#374151', sidebar_bg: '#1F2937', sidebar_text: '#D1D5DB', sidebar_active: '#34D399', header_bg: '#1F2937', header_text: '#F9FAFB', success: '#34D399', warning: '#FBBF24', danger: '#F87171', info: '#60A5FA' },
    },
    los_pelambres: {
        name: 'Los Pelambres',
        light: { primary: '#007A33', secondary: '#B87333', accent: '#0891B2', bg: '#FFFFFF', surface: '#F0FDF4', text: '#111827', text_muted: '#6B7280', border: '#BBF7D0', sidebar_bg: '#FFFFFF', sidebar_text: '#374151', sidebar_active: '#007A33', header_bg: '#FFFFFF', header_text: '#111827', success: '#10B981', warning: '#F59E0B', danger: '#EF4444', info: '#3B82F6' },
        dark: { primary: '#4ADE80', secondary: '#D4A574', accent: '#22D3EE', bg: '#0A1F0A', surface: '#162816', text: '#F0FDF4', text_muted: '#86EFAC', border: '#1E3A1E', sidebar_bg: '#162816', sidebar_text: '#BBF7D0', sidebar_active: '#4ADE80', header_bg: '#162816', header_text: '#F0FDF4', success: '#34D399', warning: '#FBBF24', danger: '#F87171', info: '#60A5FA' },
    },
};

function formatBytes(bytes) {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
}

// ── Logo Upload Slot ────────────────────────────────────────────────
function LogoSlot({ label, mode, empresa, onUploaded }) {
    const [preview, setPreview] = useState(null);
    const [origSize, setOrigSize] = useState(0);
    const [compSize, setCompSize] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [removing, setRemoving] = useState(false);
    const inputRef = useRef(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    const currentUrl = empresa?.[`logo_url_${mode}`];

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error('Formato no soportado. Use PNG, JPG, SVG o WebP.');
            return;
        }
        setOrigSize(file.size);

        // Preview
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);

        // Compress (skip SVG)
        let toUpload = file;
        if (file.type !== 'image/svg+xml') {
            try {
                const { compressImage } = await import('../lib/compressImage');
                toUpload = await compressImage(file, 512, 0.85);
                setCompSize(toUpload.size || toUpload.length || file.size);
            } catch {
                setCompSize(file.size);
            }
        } else {
            setCompSize(file.size);
        }

        // Upload
        setUploading(true);
        try {
            await empresaAPI.uploadLogo(empresa.id, mode, toUpload);
            toast.success(`Logo ${mode === 'light' ? 'claro' : 'oscuro'} guardado`);
            if (onUploaded) onUploaded();
        } catch (err) {
            toast.error('Error subiendo logo');
            console.error(err);
        } finally {
            setUploading(false);
        }
    }, [empresa, mode, onUploaded]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer?.files?.[0];
        if (file) handleFile(file);
    }, [handleFile]);

    const handleRemove = async () => {
        if (!empresa?.id) return;
        setRemoving(true);
        try {
            await empresaAPI.removeLogo(empresa.id, mode);
            setPreview(null);
            setOrigSize(0);
            setCompSize(0);
            toast.success('Logo eliminado');
            if (onUploaded) onUploaded();
        } catch (err) {
            toast.error('Error eliminando logo');
        } finally {
            setRemoving(false);
        }
    };

    const displayUrl = currentUrl ? `${backendUrl}${currentUrl}` : null;
    const showImage = preview || displayUrl;

    return (
        <div className="border rounded-lg p-4 space-y-3" data-testid={`logo-slot-${mode}`}>
            <Label className="font-medium">Logo para modo {mode === 'light' ? 'claro' : 'oscuro'}</Label>

            {/* Current / Preview */}
            {showImage && (
                <div className="flex items-center gap-4">
                    <div className="w-24 h-16 flex items-center justify-center border rounded bg-muted/30 overflow-hidden">
                        <img src={showImage} alt={`Logo ${mode}`} className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                        {origSize > 0 && <p>Original: {formatBytes(origSize)}</p>}
                        {compSize > 0 && compSize !== origSize && <p>Comprimido: {formatBytes(compSize)}</p>}
                        {currentUrl && !preview && <p className="text-green-600 font-medium">Guardado ✓</p>}
                        {preview && <p className="text-amber-600 font-medium">Vista previa (guardándose...)</p>}
                    </div>
                </div>
            )}

            {/* Drop zone / file input */}
            <div
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={handleDrop}
                data-testid={`logo-dropzone-${mode}`}
            >
                <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                    {uploading ? 'Subiendo...' : 'Click o arrastre una imagen'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG, WebP</p>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid={`logo-input-${mode}`}
                />
            </div>

            {/* Remove button */}
            {(currentUrl || preview) && (
                <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={handleRemove}
                    disabled={removing}
                    data-testid={`logo-remove-${mode}`}
                >
                    <Trash2 className="h-3 w-3 mr-1" />
                    {removing ? 'Quitando...' : 'Quitar logo'}
                </Button>
            )}
        </div>
    );
}

// ── Main Theme Editor ───────────────────────────────────────────────
export default function ThemeEditorTab() {
    const { user, empresa, loadEmpresa } = useAuth();
    const { tema: currentTema, loadTema } = useTheme();
    const [editMode, setEditMode] = useState('light');
    const [lightPalette, setLightPalette] = useState({});
    const [darkPalette, setDarkPalette] = useState({});
    const [modeDefault, setModeDefault] = useState('light');
    const [allowToggle, setAllowToggle] = useState(true);
    const [brandShowTitle, setBrandShowTitle] = useState(true);
    const [brandShowNombre, setBrandShowNombre] = useState(true);
    const [brandTitleSize, setBrandTitleSize] = useState(14);
    const [brandNombreSize, setBrandNombreSize] = useState(12);
    const [brandLogoHeight, setBrandLogoHeight] = useState(80);
    const [brandLogoTop, setBrandLogoTop] = useState(0);
    const [brandAreaHeight, setBrandAreaHeight] = useState(0);
    const [brandAreaPadding, setBrandAreaPadding] = useState(12);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (currentTema) {
            setLightPalette({ ...currentTema.light });
            setDarkPalette({ ...currentTema.dark });
            setModeDefault(currentTema.mode_default || 'light');
            setAllowToggle(currentTema.allow_user_toggle !== false);
            setBrandShowTitle(currentTema.brand_show_title !== false);
            setBrandShowNombre(currentTema.brand_show_nombre !== false);
            setBrandTitleSize(currentTema.brand_title_size || 14);
            setBrandNombreSize(currentTema.brand_nombre_size || 12);
            setBrandLogoHeight(currentTema.brand_logo_height || 80);
            setBrandLogoTop(currentTema.brand_logo_top || 0);
            setBrandAreaHeight(currentTema.brand_area_height || 0);
            setBrandAreaPadding(currentTema.brand_area_padding ?? 12);
        }
    }, [currentTema]);

    const handleColorChange = (key, value) => {
        setDirty(true);
        if (editMode === 'light') {
            setLightPalette(prev => ({ ...prev, [key]: value }));
        } else {
            setDarkPalette(prev => ({ ...prev, [key]: value }));
        }
    };

    const handleCopyPalette = (direction) => {
        setDirty(true);
        if (direction === 'light-to-dark') {
            setDarkPalette({ ...lightPalette });
            toast.success('Colores copiados de Claro → Oscuro');
        } else {
            setLightPalette({ ...darkPalette });
            toast.success('Colores copiados de Oscuro → Claro');
        }
    };

    const handlePreset = (presetKey) => {
        const preset = PRESETS[presetKey];
        if (!preset) return;
        setLightPalette({ ...preset.light });
        setDarkPalette({ ...preset.dark });
        setDirty(true);
        toast.success(`Preset "${preset.name}" aplicado`);
    };

    const handleReset = () => {
        if (currentTema) {
            setLightPalette({ ...currentTema.light });
            setDarkPalette({ ...currentTema.dark });
            setModeDefault(currentTema.mode_default || 'light');
            setAllowToggle(currentTema.allow_user_toggle !== false);
            setBrandShowTitle(currentTema.brand_show_title !== false);
            setBrandShowNombre(currentTema.brand_show_nombre !== false);
            setBrandTitleSize(currentTema.brand_title_size || 14);
            setBrandNombreSize(currentTema.brand_nombre_size || 12);
            setBrandLogoHeight(currentTema.brand_logo_height || 80);
            setBrandLogoTop(currentTema.brand_logo_top || 0);
            setBrandAreaHeight(currentTema.brand_area_height || 0);
            setBrandAreaPadding(currentTema.brand_area_padding ?? 12);
            setDirty(false);
            toast.info('Valores restaurados');
        }
    };

    const handleSave = async () => {
        if (!empresa?.id) return;
        setSaving(true);
        try {
            await empresaAPI.updateTema(empresa.id, {
                light: lightPalette,
                dark: darkPalette,
                mode_default: modeDefault,
                allow_user_toggle: allowToggle,
                brand_show_title: brandShowTitle,
                brand_show_nombre: brandShowNombre,
                brand_title_size: brandTitleSize,
                brand_nombre_size: brandNombreSize,
                brand_logo_height: brandLogoHeight,
                brand_logo_top: brandLogoTop,
                brand_area_height: brandAreaHeight,
                brand_area_padding: brandAreaPadding,
            });
            toast.success('Tema guardado exitosamente');
            setDirty(false);
            // Reload tema in context to apply CSS vars immediately
            await loadTema(empresa.id);
            // Reload empresa so BrandBlock picks up brand_* changes without refresh
            await loadEmpresa(empresa.id);
        } catch (err) {
            toast.error('Error al guardar tema');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUploaded = useCallback(async () => {
        // Refresh empresa data to get updated logo URLs
        if (empresa?.id) {
            await loadEmpresa(empresa.id);
        }
    }, [empresa?.id, loadEmpresa]);

    const activePalette = editMode === 'light' ? lightPalette : darkPalette;

    const canEdit = user?.rol === 'admin' || user?.rol === 'superadmin';
    if (!canEdit) return null;

    return (
        <div className="space-y-6" data-testid="theme-editor">
            {/* Top Controls */}
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex gap-2">
                    <Button
                        variant={editMode === 'light' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEditMode('light')}
                        data-testid="edit-light"
                    >
                        <Sun className="h-4 w-4 mr-1" /> Modo Claro
                    </Button>
                    <Button
                        variant={editMode === 'dark' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEditMode('dark')}
                        data-testid="edit-dark"
                    >
                        <Moon className="h-4 w-4 mr-1" /> Modo Oscuro
                    </Button>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleReset} data-testid="reset-theme">
                        <RotateCcw className="h-4 w-4 mr-1" /> Restaurar
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={saving || !dirty} data-testid="save-theme">
                        <Save className="h-4 w-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </div>
            </div>

            {/* Presets */}
            <div className="border rounded-lg p-4">
                <Label className="text-sm font-medium mb-2 block">Presets Rápidos</Label>
                <div className="flex flex-wrap gap-2">
                    {Object.entries(PRESETS).map(([key, preset]) => (
                        <Button
                            key={key}
                            variant="outline"
                            size="sm"
                            onClick={() => handlePreset(key)}
                            className="gap-2"
                            data-testid={`preset-${key}`}
                        >
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.light.primary }} />
                            {preset.name}
                        </Button>
                    ))}
                    <Button variant="outline" size="sm" className="gap-2" disabled>
                        <Palette className="h-3 w-3" /> Personalizado
                    </Button>
                </div>
            </div>

            {/* Copy buttons */}
            <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleCopyPalette('light-to-dark')} data-testid="copy-light-dark">
                    <Copy className="h-4 w-4 mr-1" /> Copiar Claro → Oscuro
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleCopyPalette('dark-to-light')} data-testid="copy-dark-light">
                    <Copy className="h-4 w-4 mr-1" /> Copiar Oscuro → Claro
                </Button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Color Pickers */}
                <div className="space-y-4">
                    <h3 className="font-medium flex items-center gap-2">
                        {editMode === 'light' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        Colores — {editMode === 'light' ? 'Modo Claro' : 'Modo Oscuro'}
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                        {THEME_KEYS.map(({ key, label }) => (
                            <div key={key} className="space-y-1">
                                <Label className="text-xs">{label}</Label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={activePalette[key] || '#000000'}
                                        onChange={(e) => handleColorChange(key, e.target.value)}
                                        className="w-8 h-8 rounded cursor-pointer border"
                                        data-testid={`color-${editMode}-${key}`}
                                    />
                                    <Input
                                        value={activePalette[key] || ''}
                                        onChange={(e) => handleColorChange(key, e.target.value)}
                                        className="h-8 text-xs font-mono"
                                        placeholder="#000000"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Live Preview */}
                <div className="space-y-4">
                    <h3 className="font-medium flex items-center gap-2">
                        <Eye className="h-4 w-4" /> Vista Previa
                    </h3>
                    <div
                        className="border rounded-lg overflow-hidden"
                        style={{ backgroundColor: activePalette.bg || '#fff' }}
                    >
                        {/* Preview Header */}
                        <div
                            className="flex items-center justify-between px-4 py-3 border-b"
                            style={{ backgroundColor: activePalette.header_bg, borderColor: activePalette.border }}
                        >
                            <span className="font-bold text-sm" style={{ color: activePalette.header_text }}>
                                PROTECCIÓN INDUSTRIAL
                            </span>
                        </div>

                        <div className="flex">
                            {/* Preview Sidebar */}
                            <div
                                className="w-40 min-h-[200px] border-r p-3 space-y-2"
                                style={{ backgroundColor: activePalette.sidebar_bg, borderColor: activePalette.border }}
                            >
                                <div
                                    className="px-3 py-2 rounded text-xs font-medium"
                                    style={{ backgroundColor: activePalette.sidebar_active, color: '#fff' }}
                                >
                                    Dashboard
                                </div>
                                {['Guías', 'Reportes', 'Config'].map((item) => (
                                    <div
                                        key={item}
                                        className="px-3 py-2 rounded text-xs"
                                        style={{ color: activePalette.sidebar_text }}
                                    >
                                        {item}
                                    </div>
                                ))}
                            </div>

                            {/* Preview Content */}
                            <div className="flex-1 p-4 space-y-3">
                                <div
                                    className="p-3 rounded border"
                                    style={{ backgroundColor: activePalette.surface, borderColor: activePalette.border }}
                                >
                                    <p className="text-sm font-medium" style={{ color: activePalette.text }}>Guía #001</p>
                                    <p className="text-xs mt-1" style={{ color: activePalette.text_muted }}>Pendiente de aprobación</p>
                                </div>

                                <div className="flex gap-2">
                                    <button className="px-3 py-1.5 rounded text-xs font-medium text-white" style={{ backgroundColor: activePalette.primary }}>
                                        Aprobar
                                    </button>
                                    <button className="px-3 py-1.5 rounded text-xs font-medium text-white" style={{ backgroundColor: activePalette.danger }}>
                                        Rechazar
                                    </button>
                                </div>

                                <div className="flex gap-2 flex-wrap">
                                    <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: activePalette.success }}>Aprobada</span>
                                    <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: activePalette.warning }}>Pendiente</span>
                                    <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: activePalette.danger }}>Rechazada</span>
                                    <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: activePalette.info }}>Info</span>
                                </div>

                                <div
                                    className="flex items-center justify-between p-2 rounded border text-xs"
                                    style={{ borderColor: activePalette.border, color: activePalette.text }}
                                >
                                    <span>Material ABC</span>
                                    <span style={{ color: activePalette.text_muted }}>2024-01-15</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mode & Toggle Settings */}
            <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium">Configuración General</h3>
                <div className="flex items-center justify-between">
                    <div>
                        <Label>Modo por defecto</Label>
                        <p className="text-xs text-muted-foreground">Qué modo ven los usuarios al entrar</p>
                    </div>
                    <select
                        value={modeDefault}
                        onChange={(e) => { setModeDefault(e.target.value); setDirty(true); }}
                        className="border rounded px-3 py-1.5 text-sm bg-background"
                        data-testid="mode-default-select"
                    >
                        <option value="light">Claro</option>
                        <option value="dark">Oscuro</option>
                        <option value="system">Seguir Sistema</option>
                    </select>
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <Label>Permitir al usuario cambiar de modo</Label>
                        <p className="text-xs text-muted-foreground">Muestra el botón Sol/Luna en el header</p>
                    </div>
                    <Switch
                        checked={allowToggle}
                        onCheckedChange={(v) => { setAllowToggle(v); setDirty(true); }}
                        data-testid="allow-toggle-switch"
                    />
                </div>
            </div>

            {/* Brand / Sidebar Display */}
            <div className="border rounded-lg p-4 space-y-4" data-testid="brand-settings">
                <h3 className="font-medium flex items-center gap-2">
                    <Type className="h-4 w-4" /> Sidebar — Marca
                </h3>
                <p className="text-xs text-muted-foreground">
                    Configure qué elementos se muestran en el sidebar y sus tamaños.
                </p>

                {/* Toggle: Show title */}
                <div className="flex items-center justify-between">
                    <div>
                        <Label>Mostrar "Protección Industrial"</Label>
                        <p className="text-xs text-muted-foreground">Título principal bajo el logo</p>
                    </div>
                    <Switch
                        checked={brandShowTitle}
                        onCheckedChange={(v) => { setBrandShowTitle(v); setDirty(true); }}
                        data-testid="brand-show-title"
                    />
                </div>

                {/* Toggle: Show nombre_corto */}
                <div className="flex items-center justify-between">
                    <div>
                        <Label>Mostrar nombre corto ({empresa?.nombre_corto || 'N/A'})</Label>
                        <p className="text-xs text-muted-foreground">Subtítulo de la empresa</p>
                    </div>
                    <Switch
                        checked={brandShowNombre}
                        onCheckedChange={(v) => { setBrandShowNombre(v); setDirty(true); }}
                        data-testid="brand-show-nombre"
                    />
                </div>

                {/* Size: Logo height */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                            <Ruler className="h-3 w-3" /> Alto del logo
                        </Label>
                        <span className="text-xs font-mono text-muted-foreground">{brandLogoHeight}px</span>
                    </div>
                    <input
                        type="range"
                        min={30}
                        max={120}
                        step={2}
                        value={brandLogoHeight}
                        onChange={(e) => { setBrandLogoHeight(Number(e.target.value)); setDirty(true); }}
                        className="w-full accent-primary"
                        data-testid="brand-logo-height"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>30px</span><span>120px</span>
                    </div>
                </div>

                {/* Size: Title */}
                {brandShowTitle && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-1">
                                <Ruler className="h-3 w-3" /> Tamaño "Protección Industrial"
                            </Label>
                            <span className="text-xs font-mono text-muted-foreground">{brandTitleSize}px</span>
                        </div>
                        <input
                            type="range"
                            min={10}
                            max={24}
                            step={1}
                            value={brandTitleSize}
                            onChange={(e) => { setBrandTitleSize(Number(e.target.value)); setDirty(true); }}
                            className="w-full accent-primary"
                            data-testid="brand-title-size"
                        />
                        <div className="flex justify-between text-[10px] text-muted-foreground">
                            <span>10px</span><span>24px</span>
                        </div>
                    </div>
                )}

                {/* Size: Nombre corto */}
                {brandShowNombre && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-1">
                                <Ruler className="h-3 w-3" /> Tamaño nombre corto
                            </Label>
                            <span className="text-xs font-mono text-muted-foreground">{brandNombreSize}px</span>
                        </div>
                        <input
                            type="range"
                            min={8}
                            max={20}
                            step={1}
                            value={brandNombreSize}
                            onChange={(e) => { setBrandNombreSize(Number(e.target.value)); setDirty(true); }}
                            className="w-full accent-primary"
                            data-testid="brand-nombre-size"
                        />
                        <div className="flex justify-between text-[10px] text-muted-foreground">
                            <span>8px</span><span>20px</span>
                        </div>
                    </div>
                )}

                {/* Position: Logo vertical offset */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                            <Ruler className="h-3 w-3" /> Posición vertical del logo
                        </Label>
                        <span className="text-xs font-mono text-muted-foreground">{brandLogoTop}px</span>
                    </div>
                    <input
                        type="range"
                        min={0}
                        max={40}
                        step={2}
                        value={brandLogoTop}
                        onChange={(e) => { setBrandLogoTop(Number(e.target.value)); setDirty(true); }}
                        className="w-full accent-primary"
                        data-testid="brand-logo-top"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>0px</span><span>40px</span>
                    </div>
                </div>

                {/* Height: Brand area */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                            <Ruler className="h-3 w-3" /> Altura del área de marca
                        </Label>
                        <span className="text-xs font-mono text-muted-foreground">{brandAreaHeight === 0 ? 'Auto' : `${brandAreaHeight}px`}</span>
                    </div>
                    <input
                        type="range"
                        min={0}
                        max={250}
                        step={5}
                        value={brandAreaHeight}
                        onChange={(e) => { setBrandAreaHeight(Number(e.target.value)); setDirty(true); }}
                        className="w-full accent-primary"
                        data-testid="brand-area-height"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>Auto</span><span>250px</span>
                    </div>
                </div>

                {/* Padding: Brand area */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                            <Ruler className="h-3 w-3" /> Margen interno del área
                        </Label>
                        <span className="text-xs font-mono text-muted-foreground">{brandAreaPadding}px</span>
                    </div>
                    <input
                        type="range"
                        min={0}
                        max={32}
                        step={2}
                        value={brandAreaPadding}
                        onChange={(e) => { setBrandAreaPadding(Number(e.target.value)); setDirty(true); }}
                        className="w-full accent-primary"
                        data-testid="brand-area-padding"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>0px</span><span>32px</span>
                    </div>
                </div>

                {/* Live mini-preview of brand block */}
                <div className="border rounded-lg p-4 bg-muted/30">
                    <Label className="text-xs text-muted-foreground mb-2 block">Vista previa sidebar</Label>
                    <div
                        className="flex flex-col items-start gap-1 border border-dashed border-muted-foreground/30 rounded"
                        style={{
                            minHeight: brandAreaHeight > 0 ? `${brandAreaHeight * 0.5}px` : 'auto',
                            padding: `${brandAreaPadding * 0.5}px`,
                        }}
                    >
                        <div
                            className="bg-muted rounded w-16"
                            style={{
                                height: `${brandLogoHeight * 0.6}px`,
                                marginTop: `${brandLogoTop * 0.5}px`,
                            }}
                        >
                            <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">Logo</div>
                        </div>
                        {brandShowTitle && (
                            <span
                                className="font-black uppercase tracking-tight text-primary whitespace-nowrap"
                                style={{ fontSize: `${brandTitleSize}px` }}
                            >
                                PROTECCIÓN INDUSTRIAL
                            </span>
                        )}
                        {brandShowNombre && (
                            <span
                                className="text-muted-foreground uppercase tracking-wider"
                                style={{ fontSize: `${brandNombreSize}px` }}
                            >
                                {empresa?.nombre_corto || 'MLP'}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Logo Upload */}
            <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" /> Logos por Modo
                </h3>
                <p className="text-xs text-muted-foreground">
                    Suba un logo diferente para cada modo. Se muestra en el sidebar según el modo activo.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                    <LogoSlot label="Logo modo claro" mode="light" empresa={empresa} onUploaded={handleLogoUploaded} />
                    <LogoSlot label="Logo modo oscuro" mode="dark" empresa={empresa} onUploaded={handleLogoUploaded} />
                </div>
            </div>
        </div>
    );
}
